import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  Tag,
  Space,
  Button,
  Row,
  Col,
  Statistic,
  Spin,
  Descriptions,
  theme,
  Alert,
  Tabs,
  Empty,
  Modal,
  Divider,
  Progress,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  DollarOutlined,
  TeamOutlined,
  UserOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  SwapOutlined,
  BankOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";
import { authenticatedFetch } from "@/libs/api";

const { Title, Text } = Typography;

// API helper
const fetchWithAuth = async (url, options = {}) => {
  const response = await authenticatedFetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || err.error || `HTTP ${response.status}`);
  }
  return response.json();
};

// Status configuration
const statusConfig = {
  COMPLETED: { color: "success", icon: <CheckCircleOutlined />, text: "Completed" },
  RUNNING: { color: "processing", icon: <SyncOutlined spin />, text: "Running" },
  PENDING: { color: "default", icon: <ClockCircleOutlined />, text: "Pending" },
  FAILED: { color: "error", icon: <CloseCircleOutlined />, text: "Failed" },
  CANCELLED: { color: "warning", icon: <CloseCircleOutlined />, text: "Cancelled" },
};

// Run type configuration
const runTypeConfig = {
  FINAL: { color: "blue", text: "Final" },
  PREVIEW: { color: "cyan", text: "Preview" },
  RERUN: { color: "orange", text: "Rerun" },
  BACKFILL: { color: "purple", text: "Backfill" },
};

// Scope configuration
const scopeConfig = {
  FULL: { color: "geekblue", text: "Full" },
  CONTROL_GROUP: { color: "green", text: "Control Group" },
  VIEW_GROUP: { color: "lime", text: "View Group" },
  CUSTOMER: { color: "gold", text: "Customer" },
};


export default function SettlementRunDetail() {
  const { token } = theme.useToken();
  const { runId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [runDetail, setRunDetail] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [groupBreakdown, setGroupBreakdown] = useState([]);
  const [flowData, setFlowData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [groupPageSize, setGroupPageSize] = useState(10);
  const [ledgerPageSize, setLedgerPageSize] = useState(15);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch run detail
        const runResult = await fetchWithAuth(`/api/settlements/runs/${runId}`);
        if (runResult.r === 0 && runResult.data) {
          setRunDetail(runResult.data);

          // Fetch ledger transactions for this run
          const txResult = await fetchWithAuth(
            `/api/settlements/transactions?sourceRunId=${runId}&limit=500&includeAllVersions=true`
          );
          if (txResult.r === 0 && txResult.data) {
            setLedgerEntries(txResult.data);
          }

          // Fetch group breakdown
          const groupResult = await fetchWithAuth(`/api/settlements/runs/${runId}/groups`);
          if (groupResult.r === 0 && groupResult.data) {
            setGroupBreakdown(groupResult.data);
          }

          // Fetch settlement flow data
          const flowResult = await fetchWithAuth(`/api/settlements/runs/${runId}/flow`);
          if (flowResult.r === 0 && flowResult.data) {
            setFlowData(flowResult.data);
          }
        } else {
          message.error("Settlement run not found");
        }
      } catch (error) {
        console.error("Failed to fetch settlement run detail:", error);
        message.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (runId) {
      fetchData();
    }
  }, [runId]);

  const handleRerun = () => {
    Modal.confirm({
      title: "Confirm Rerun",
      content: `Are you sure you want to rerun settlement for ${runDetail?.run_date}? This will create a new settlement run with type RERUN.`,
      okText: "Rerun",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const result = await fetchWithAuth(`/api/settlements/runs/trigger`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              runDate: runDetail?.run_date,
              runScope: runDetail?.run_scope || "FULL",
              runType: "RERUN",
              notes: `Rerun of ${runId}`,
            }),
          });
          Modal.success({
            title: "Settlement Rerun Triggered",
            content: `New Run ID: ${result.data?.run_id || "Unknown"}`,
          });
          // Navigate to the new run
          if (result.data?.run_id) {
            navigate(`/settlements/runs/${result.data.run_id}`);
          }
        } catch (error) {
          message.error(`Failed to trigger rerun: ${error.message}`);
        }
      },
    });
  };

  const handleCancel = () => {
    Modal.confirm({
      title: "Cancel Settlement Run",
      content: `Are you sure you want to cancel this settlement run? This will stop any pending processing.`,
      okText: "Yes, Cancel Run",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: async () => {
        try {
          await fetchWithAuth(`/api/settlements/runs/${runId}/cancel`, {
            method: "PUT",
          });
          message.success("Settlement run cancelled");
          // Refresh data
          setRunDetail({ ...runDetail, status: "CANCELLED" });
        } catch (error) {
          message.error(error.message);
        }
      },
    });
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Delete Settlement Run",
      content: (
        <div>
          <p>Are you sure you want to delete this settlement run?</p>
          <p style={{ color: "#ff4d4f" }}>
            This will permanently delete:
            <ul>
              <li>The settlement run record</li>
              <li>All associated ledger transactions</li>
              <li>All company/customer daily ledger entries for this run</li>
            </ul>
          </p>
          <p><strong>This action cannot be undone.</strong></p>
        </div>
      ),
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await fetchWithAuth(`/api/settlements/runs/${runId}`, {
            method: "DELETE",
          });
          message.success("Settlement run deleted");
          navigate("/settlements/runs");
        } catch (error) {
          message.error(error.message);
        }
      },
    });
  };

  // Ledger entries columns
  const ledgerColumns = [
    {
      title: "ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 80,
      render: (id) => <Text code style={{ fontSize: 10 }}>{id}</Text>,
    },
    {
      title: "Debit (From)",
      key: "debit",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{record.debit_entity_id?.slice(0, 16)}</Text>
          <Tag color="red" style={{ fontSize: 10 }}>{record.debit_ledger_type}</Tag>
        </Space>
      ),
    },
    {
      title: "Credit (To)",
      key: "credit",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{record.credit_entity_id?.slice(0, 16)}</Text>
          <Tag color="green" style={{ fontSize: 10 }}>{record.credit_ledger_type}</Tag>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "transaction_type",
      key: "transaction_type",
      width: 140,
      render: (type) => <Tag color="purple" style={{ fontSize: 10 }}>{type}</Tag>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      align: "right",
      render: (val) => (
        <Text style={{ color: token.colorSuccess, fontWeight: 600 }}>
          ${Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
  ];

  // Group breakdown columns
  const groupColumns = [
    {
      title: "Group",
      key: "group",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.group_name}</Text>
          {record.duid && <Tag color="blue">{record.duid}</Tag>}
        </Space>
      ),
    },
    {
      title: "Systems",
      dataIndex: "system_count",
      key: "system_count",
      align: "center",
      render: (count, record) => (
        <Space>
          <Tag color="cyan">{count || 0}</Tag>
          {record.online_count !== undefined && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              ({record.online_count} online)
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Total Power",
      dataIndex: "total_power_kw",
      key: "total_power_kw",
      align: "right",
      render: (val) => `${Number(val || 0).toLocaleString()} kW`,
    },
    {
      title: "Revenue Allocated",
      dataIndex: "revenue_allocated",
      key: "revenue_allocated",
      align: "right",
      render: (val) => (
        <Text style={{ color: token.colorSuccess, fontWeight: 600 }}>
          ${Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => (
        <Tag color={status === "COMPLETED" ? "success" : "warning"}>
          {status || "COMPLETED"}
        </Tag>
      ),
    },
  ];

  // Summary calculations
  const totalAmount = useMemo(() => ledgerEntries.reduce((sum, e) => sum + Number(e.amount || 0), 0), [ledgerEntries]);
  const totalGroupRevenue = useMemo(() => groupBreakdown.reduce((sum, g) => sum + Number(g.revenue_allocated || 0), 0), [groupBreakdown]);
  const totalGroupSystems = useMemo(() => groupBreakdown.reduce((sum, g) => sum + Number(g.system_count || 0), 0), [groupBreakdown]);

  if (loading) {
    return (
      <PageLayout active="settlementRuns">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!runDetail) {
    return (
      <PageLayout active="settlementRuns">
        <Empty description="Settlement run not found" />
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Button onClick={() => navigate("/settlements/runs")}>
            Back to Settlement Runs
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="settlementRuns">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/settlements/runs")}
          style={{ marginBottom: 8, padding: 0 }}
        >
          Back to Settlement Runs
        </Button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              <FileTextOutlined style={{ marginRight: 12, color: token.colorPrimary }} />
              Settlement Run Detail
            </Title>
            <Space>
              <Text code style={{ fontSize: 14 }}>{runDetail.run_id}</Text>
              <Tag color={statusConfig[runDetail.status]?.color} icon={statusConfig[runDetail.status]?.icon}>
                {statusConfig[runDetail.status]?.text}
              </Tag>
            </Space>
          </div>
          <Space>
            {(runDetail.status === "PENDING" || runDetail.status === "RUNNING") && (
              <Button danger icon={<StopOutlined />} onClick={handleCancel}>
                Cancel Run
              </Button>
            )}
            {runDetail.status === "FAILED" && (
              <Button type="primary" icon={<ReloadOutlined />} onClick={handleRerun}>
                Rerun Settlement
              </Button>
            )}
            <Button danger type="text" icon={<DeleteOutlined />} onClick={handleDelete}>
              Delete
            </Button>
          </Space>
        </div>
      </div>

      {/* Error Alert */}
      {runDetail.error_message && (
        <Alert
          message="Settlement Run Failed"
          description={runDetail.error_message}
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 24 }}
          action={
            <Button size="small" danger onClick={handleRerun}>
              Retry
            </Button>
          }
        />
      )}

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Credit"
              value={Number(runDetail.total_credit) || 0}
              precision={2}
              prefix="$"
              valueStyle={{ color: token.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Debit"
              value={Number(runDetail.total_debit) || 0}
              precision={2}
              prefix="$"
              valueStyle={{ color: token.colorError }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Net Movement"
              value={(Number(runDetail.total_credit) || 0) - (Number(runDetail.total_debit) || 0)}
              precision={2}
              prefix="$"
              valueStyle={{ color: (Number(runDetail.total_credit) || 0) - (Number(runDetail.total_debit) || 0) >= 0 ? token.colorSuccess : token.colorError }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Duration"
              value={runDetail.duration_seconds ? `${Math.floor(runDetail.duration_seconds / 60)}m ${runDetail.duration_seconds % 60}s` : "In Progress"}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "overview",
              label: (
                <Space>
                  <FileTextOutlined />
                  Overview
                </Space>
              ),
              children: (
                <div>
                  <Row gutter={24}>
                    <Col xs={24} lg={12}>
                      <Descriptions
                        title="Run Information"
                        column={1}
                        bordered
                        size="small"
                        style={{ marginBottom: 24 }}
                      >
                        <Descriptions.Item label="Run ID">
                          <Text code>{runDetail.run_id}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Run Date">
                          <Text strong>{dayjs(runDetail.run_date).format("YYYY-MM-DD")}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Run Type">
                          <Tag color={runTypeConfig[runDetail.run_type]?.color}>
                            {runTypeConfig[runDetail.run_type]?.text}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Scope">
                          <Tag color={scopeConfig[runDetail.run_scope]?.color}>
                            {scopeConfig[runDetail.run_scope]?.text}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Triggered By">
                          <Tag color={runDetail.triggered_by === "SCHEDULER" ? "blue" : "orange"}>
                            {runDetail.triggered_by === "SCHEDULER"
                              ? "Scheduler"
                              : (runDetail.triggerer_name || runDetail.triggered_by?.replace("MANUAL:", ""))}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Rule Snapshot">
                          {runDetail.rule_snapshot_hash
                            ? <Text code style={{ fontSize: 11 }}>{runDetail.rule_snapshot_hash}</Text>
                            : <Text type="secondary">-</Text>}
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Descriptions
                        title="Processing Summary"
                        column={1}
                        bordered
                        size="small"
                        style={{ marginBottom: 24 }}
                      >
                        <Descriptions.Item label="Started At">
                          {dayjs(runDetail.started_at).format("YYYY-MM-DD HH:mm:ss")}
                        </Descriptions.Item>
                        <Descriptions.Item label="Finished At">
                          {runDetail.finished_at ? dayjs(runDetail.finished_at).format("YYYY-MM-DD HH:mm:ss") : <Tag color="processing">In Progress</Tag>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Systems Processed">
                          <Tag color="cyan" icon={<SettingOutlined />}>{(runDetail.systems_processed || 0).toLocaleString()}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Groups Processed">
                          <Tag color="green" icon={<TeamOutlined />}>{(runDetail.groups_processed || 0).toLocaleString()}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Customers Processed">
                          <Tag color="gold" icon={<UserOutlined />}>{(runDetail.customers_processed || 0).toLocaleString()}</Tag>
                        </Descriptions.Item>
                        {runDetail.notes && (
                          <Descriptions.Item label="Notes">
                            <Text type="secondary">{runDetail.notes}</Text>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Col>
                  </Row>

                  {/* Processing Progress */}
                  <Card
                    title="Processing Breakdown"
                    size="small"
                    bordered={false}
                    style={{ background: token.colorFillQuaternary, marginTop: 16 }}
                  >
                    <Row gutter={32}>
                      <Col xs={24} md={8}>
                        <div style={{ textAlign: "center" }}>
                          <Progress
                            type="circle"
                            percent={100}
                            format={() => runDetail.groups_processed || 0}
                            strokeColor={token.colorSuccess}
                            size={80}
                          />
                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary">Control Groups</Text>
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} md={8}>
                        <div style={{ textAlign: "center" }}>
                          <Progress
                            type="circle"
                            percent={100}
                            format={() => runDetail.systems_processed || 0}
                            strokeColor={token.colorPrimary}
                            size={80}
                          />
                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary">Systems</Text>
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} md={8}>
                        <div style={{ textAlign: "center" }}>
                          <Progress
                            type="circle"
                            percent={100}
                            format={() => runDetail.customers_processed || 0}
                            strokeColor={token.colorWarning}
                            size={80}
                          />
                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary">Customers</Text>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </div>
              ),
            },
            {
              key: "flow",
              label: (
                <Space>
                  <SwapOutlined />
                  Settlement Flow
                </Space>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                      Shows the 4-step settlement process: how money flows from AEMO through groups, systems, companies, and to customers.
                    </Text>
                  </div>

                  {/* Platform Summary Cards */}
                  {flowData?.powow && (
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ background: "#fff", border: "1px solid #e8e8e8" }}>
                          <Statistic
                            title={<Space><ThunderboltOutlined /> Platform Total Credit</Space>}
                            value={flowData.powow.totalCredit || 0}
                            precision={2}
                            prefix="$"
                            valueStyle={{ color: token.colorSuccess, fontSize: 22 }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ background: "#fff", border: "1px solid #e8e8e8" }}>
                          <Statistic
                            title={<Space><ThunderboltOutlined /> Platform Total Debit</Space>}
                            value={flowData.powow.totalDebit || 0}
                            precision={2}
                            prefix="$"
                            valueStyle={{ color: token.colorError, fontSize: 22 }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ background: "#fff", border: "1px solid #e8e8e8" }}>
                          <Statistic
                            title={<Space><ThunderboltOutlined /> Platform Net Moving</Space>}
                            value={flowData.powow.netMoving || 0}
                            precision={2}
                            prefix="$"
                            valueStyle={{
                              color: (flowData.powow.netMoving || 0) >= 0 ? token.colorSuccess : token.colorError,
                              fontSize: 22
                            }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {/* Step-by-step flow */}
                  <Row gutter={[16, 16]}>
                    {/* Step 2: Group Revenue */}
                    <Col span={24}>
                      <Card
                        title={<Space><Tag color="blue">Step 2</Tag> Group Revenue</Space>}
                        size="small"
                        extra={<Text type="secondary">AEMO → Platform/Groups</Text>}
                      >
                        {flowData?.steps?.step2_group_revenue?.summary && (
                          <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={12}>
                              <Statistic
                                title="Groups Processed"
                                value={flowData.steps.step2_group_revenue.summary.groups_processed || 0}
                                valueStyle={{ color: token.colorPrimary }}
                              />
                            </Col>
                            <Col span={12}>
                              <Statistic
                                title="Total Revenue"
                                value={flowData.steps.step2_group_revenue.summary.total_revenue || 0}
                                precision={2}
                                prefix="$"
                                valueStyle={{ color: token.colorSuccess }}
                              />
                            </Col>
                          </Row>
                        )}
                        {flowData?.steps?.step2_group_revenue?.flows?.length > 0 ? (
                          <Table
                            dataSource={flowData.steps.step2_group_revenue.flows}
                            columns={[
                              { title: "From", key: "debit", render: (_, r) => <Tag color="red">{r.debit_entity_name || r.debit_ledger_type}</Tag> },
                              { title: "To", key: "credit", render: (_, r) => <Tag color="green">{r.credit_entity_name || r.credit_ledger_type}</Tag> },
                              { title: "Transactions", dataIndex: "transaction_count", key: "count", align: "center" },
                              { title: "Amount", dataIndex: "total_amount", key: "amount", align: "right", render: (v) => <Text style={{ color: token.colorSuccess, fontWeight: 600 }}>${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
                            ]}
                            rowKey={(r, i) => `step2-${i}`}
                            pagination={false}
                            size="small"
                          />
                        ) : !flowData?.steps?.step2_group_revenue?.summary ? (
                          <Empty description="No group revenue transactions" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : null}
                      </Card>
                    </Col>

                    {/* Step 3: System Allocation */}
                    <Col span={24}>
                      <Card
                        title={<Space><Tag color="green">Step 3</Tag> System Allocation</Space>}
                        size="small"
                        extra={<Text type="secondary">Groups → Systems</Text>}
                      >
                        {flowData?.steps?.step3_system_allocation?.summary ? (
                          <div>
                            <Row gutter={16}>
                              <Col span={12}>
                                <Statistic
                                  title="Systems Processed"
                                  value={flowData.steps.step3_system_allocation.summary.systems_processed || 0}
                                  valueStyle={{ color: token.colorPrimary }}
                                />
                              </Col>
                              <Col span={12}>
                                <Statistic
                                  title="Total Allocated"
                                  value={flowData.steps.step3_system_allocation.summary.total_allocated || 0}
                                  precision={2}
                                  prefix="$"
                                  valueStyle={{ color: token.colorSuccess }}
                                />
                              </Col>
                            </Row>
                            <div style={{ marginTop: 12 }}>
                              <Text type="secondary">
                                System allocation distributes group revenue to individual systems based on their performance metrics.
                                This step writes to np_system_daily_snapshot rather than creating ledger transactions.
                              </Text>
                            </div>
                          </div>
                        ) : (
                          <Empty description="No system allocation data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </Card>
                    </Col>

                    {/* Step 4: Company & Customer */}
                    <Col span={24}>
                      <Card
                        title={<Space><Tag color="purple">Step 4</Tag> Company & Customer Settlement</Space>}
                        size="small"
                        extra={<Text type="secondary">Platform/Companies → Companies/Customers</Text>}
                      >
                        {flowData?.steps?.step4_company_customer?.flows?.length > 0 ? (
                          <Table
                            dataSource={flowData.steps.step4_company_customer.flows}
                            columns={[
                              { title: "Rule Type", dataIndex: "rule_type", key: "rule", render: (v) => <Tag color="orange">{v}</Tag> },
                              { title: "From", key: "debit", render: (_, r) => <Tag color="red">{r.debit_entity_name || r.debit_ledger_type}</Tag> },
                              { title: "To", key: "credit", render: (_, r) => <Tag color="green">{r.credit_entity_name || r.credit_ledger_type}</Tag> },
                              { title: "Transactions", dataIndex: "transaction_count", key: "count", align: "center" },
                              { title: "Amount", dataIndex: "total_amount", key: "amount", align: "right", render: (v) => <Text style={{ color: token.colorSuccess, fontWeight: 600 }}>${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
                            ]}
                            rowKey={(r, i) => `step4-${i}`}
                            pagination={false}
                            size="small"
                          />
                        ) : flowData?.steps?.step4_company_customer?.summary ? (
                          <div>
                            <Row gutter={16}>
                              <Col span={6}>
                                <Statistic
                                  title="Companies Processed"
                                  value={flowData.steps.step4_company_customer.summary.companies_processed || 0}
                                  valueStyle={{ color: token.colorPrimary }}
                                />
                              </Col>
                              <Col span={6}>
                                <Statistic
                                  title="Customers Processed"
                                  value={flowData.steps.step4_company_customer.summary.customers_processed || 0}
                                  valueStyle={{ color: token.colorPrimary }}
                                />
                              </Col>
                              <Col span={6}>
                                <Statistic
                                  title="Platform Fees"
                                  value={flowData.steps.step4_company_customer.summary.total_platform_fee || 0}
                                  precision={2}
                                  prefix="$"
                                  valueStyle={{ color: token.colorWarning }}
                                />
                              </Col>
                              <Col span={6}>
                                <Statistic
                                  title="Customer Payouts"
                                  value={flowData.steps.step4_company_customer.summary.total_customer_payout || 0}
                                  precision={2}
                                  prefix="$"
                                  valueStyle={{ color: token.colorSuccess }}
                                />
                              </Col>
                            </Row>
                            <div style={{ marginTop: 12 }}>
                              <Text type="secondary">
                                No pricing rules applied. Configure company revenue share rules and customer pricing rules to enable company fees and customer payouts.
                              </Text>
                            </div>
                          </div>
                        ) : (
                          <Empty description="No company/customer transactions" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </Card>
                    </Col>
                  </Row>

                  {/* Top Credits & Debits */}
                  <Divider orientation="left">Top Entities</Divider>
                  <Row gutter={16}>
                    <Col xs={24} lg={12}>
                      <Card title="Top Credits" size="small" style={{ marginBottom: 16 }}>
                        {flowData?.topCredits?.length > 0 ? (
                          <Table
                            dataSource={flowData.topCredits.slice(0, 10)}
                            columns={[
                              { title: "Type", dataIndex: "ledger_type", key: "type", width: 100, render: (v) => <Tag>{v}</Tag> },
                              { title: "Entity", key: "entity", ellipsis: true, render: (_, r) => <Text strong style={{ fontSize: 12 }}>{r.entity_name || r.entity_id?.slice(0, 20)}</Text> },
                              { title: "Amount", dataIndex: "total_credit", key: "amount", align: "right", render: (v) => <Text style={{ color: token.colorSuccess }}>${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
                            ]}
                            rowKey={(r, i) => `credit-${i}`}
                            pagination={false}
                            size="small"
                          />
                        ) : (
                          <Empty description="No credits" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Card title="Top Debits" size="small" style={{ marginBottom: 16 }}>
                        {flowData?.topDebits?.length > 0 ? (
                          <Table
                            dataSource={flowData.topDebits.slice(0, 10)}
                            columns={[
                              { title: "Type", dataIndex: "ledger_type", key: "type", width: 100, render: (v) => <Tag>{v}</Tag> },
                              { title: "Entity", key: "entity", ellipsis: true, render: (_, r) => <Text strong style={{ fontSize: 12 }}>{r.entity_name || r.entity_id?.slice(0, 20)}</Text> },
                              { title: "Amount", dataIndex: "total_debit", key: "amount", align: "right", render: (v) => <Text style={{ color: token.colorError }}>${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
                            ]}
                            rowKey={(r, i) => `debit-${i}`}
                            pagination={false}
                            size="small"
                          />
                        ) : (
                          <Empty description="No debits" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </Card>
                    </Col>
                  </Row>

                  {/* Settlement Totals Summary */}
                  {flowData?.totals && (
                    <Card
                      size="small"
                      style={{
                        marginTop: 24,
                        background: "linear-gradient(135deg, #f6f8fc 0%, #e8f4f8 100%)",
                        border: "1px solid #d9e8f6"
                      }}
                    >
                      <Row gutter={[24, 16]} align="middle" justify="center">
                        <Col xs={24} sm={8} style={{ textAlign: "center" }}>
                          <Statistic
                            title={<Text strong>Settlement Total Credit</Text>}
                            value={flowData.totals.totalCredit || 0}
                            precision={2}
                            prefix="$"
                            valueStyle={{ color: token.colorSuccess, fontSize: 24, fontWeight: 600 }}
                          />
                        </Col>
                        <Col xs={24} sm={8} style={{ textAlign: "center" }}>
                          <Statistic
                            title={<Text strong>Settlement Total Debit</Text>}
                            value={flowData.totals.totalDebit || 0}
                            precision={2}
                            prefix="$"
                            valueStyle={{ color: token.colorError, fontSize: 24, fontWeight: 600 }}
                          />
                        </Col>
                        <Col xs={24} sm={8} style={{ textAlign: "center" }}>
                          <Statistic
                            title={<Text strong>Settlement Net</Text>}
                            value={flowData.totals.netMoving || 0}
                            precision={2}
                            prefix="$"
                            valueStyle={{
                              color: (flowData.totals.netMoving || 0) >= 0 ? token.colorSuccess : token.colorError,
                              fontSize: 24,
                              fontWeight: 600
                            }}
                          />
                          <Text type="secondary" style={{ fontSize: 11 }}>(Credit - Debit = Balanced)</Text>
                        </Col>
                      </Row>
                    </Card>
                  )}
                </div>
              ),
            },
            {
              key: "groups",
              label: (
                <Space>
                  <TeamOutlined />
                  Group Breakdown ({groupBreakdown.length})
                </Space>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                      Revenue allocated to each control group based on their power contribution and online availability.
                    </Text>
                  </div>
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                      <Statistic
                        title="Total Groups"
                        value={groupBreakdown.length}
                        prefix={<TeamOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Total Systems"
                        value={totalGroupSystems}
                        prefix={<SettingOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Total Revenue Allocated"
                        value={totalGroupRevenue}
                        precision={2}
                        prefix="$"
                        valueStyle={{ color: token.colorSuccess }}
                      />
                    </Col>
                  </Row>
                  <Table
                    columns={groupColumns}
                    dataSource={groupBreakdown}
                    rowKey={(record) => record.group_id || record.control_group_id}
                    pagination={makePagination(groupPageSize, setGroupPageSize)}
                    size="small"
                  />
                </div>
              ),
            },
            {
              key: "ledger",
              label: (
                <Space>
                  <DollarOutlined />
                  Ledger Entries ({ledgerEntries.length})
                </Space>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                      All ledger entries generated by this settlement run. Each entry represents a debit or credit to an account.
                    </Text>
                  </div>
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={12}>
                      <Statistic
                        title="Total Transactions"
                        value={ledgerEntries.length}
                        prefix={<FileTextOutlined />}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Total Amount"
                        value={totalAmount}
                        precision={2}
                        prefix="$"
                        valueStyle={{ color: token.colorSuccess }}
                      />
                    </Col>
                  </Row>
                  <Table
                    columns={ledgerColumns}
                    dataSource={ledgerEntries}
                    rowKey="transaction_id"
                    pagination={makePagination(ledgerPageSize, setLedgerPageSize)}
                    size="small"
                    scroll={{ x: 1000 }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>
    </PageLayout>
  );
}
